import { toastController } from '@ionic/core';
import { FileDoc, FilePageDoc, FileType, PhotoDoc, ScrapbookElementDoc, SubmittalBasic } from 'common';
import { ChangeEvent, ConcourseDb, PutResponse } from '../ConcourseDb';
import { LocalDb } from '../LocalDb';
import { blobToBase64 } from '../utils/data';
import { authFetch, authInterceptor, getAccessToken } from './AuthService';

export interface PageAnalysis {
  _id: string;
  drawingNum: string;
  title: string;
  description: string;
  tags: string[];
  warnings?: string[];
}

export interface SearchResult {
  snippets: string[];
  page: FilePageDoc;
}

export interface PctRect {
  text: string;
  t: number;
  r: number;
  b: number;
  l: number;
}

/** move to common */
export interface AISearchResult {
  reference: string;
  pageNumber: number;
  summary: string;
}

export class FileService {
  /**
   * Replaces matches only in text outside XML/HTML angle-bracket regions (does not wrap inside tags).
   */
  private static replaceOutsideTags(html: string, regex: RegExp, replacer: (match: string) => string): string {
    const flags = regex.flags.includes('g') ? regex.flags : regex.flags + 'g';
    let out = '';
    let inTag = false;
    let segmentStart = 0;
    for (let j = 0; j < html.length; j++) {
      const c = html[j];
      if (!inTag && c === '<') {
        if (j > segmentStart) {
          out += html.slice(segmentStart, j).replace(new RegExp(regex.source, flags), replacer);
        }
        inTag = true;
        segmentStart = j;
      } else if (inTag && c === '>') {
        out += html.slice(segmentStart, j + 1);
        inTag = false;
        segmentStart = j + 1;
      }
    }
    const tail = html.slice(segmentStart);
    out += inTag ? tail : tail.replace(new RegExp(regex.source, flags), replacer);
    return out;
  }

  static getFilesDb(projectId: string): ConcourseDb {
    return ConcourseDb.getInstance(`p-${projectId}-files`);
  }

  static async getFiles(projectId: string, onComplete, onChange, onError): Promise<FileDoc[]> {
    const db = FileService.getFilesDb(projectId);
    db.on('complete', docs => onComplete(docs));
    db.on('change', event => onChange(event.docs));
    db.on('error', err => onError(err));
    return await db.getDocs();
  }

  static getFilePagesDbName(projectId: string, fileId: string): string {
    return `p-${projectId}-file-${fileId}-pages`;
  }

  static getFilePagesDb(projectId: string, fileId: string): ConcourseDb {
    return ConcourseDb.getInstance(this.getFilePagesDbName(projectId, fileId));
  }
  static getFileMarkupsDb(projectId: string, fileId: string): ConcourseDb {
    return ConcourseDb.getInstance(`p-${projectId}-file-${fileId}-markups`);
  }

  static stopFilePagesDb(projectId: string, fileId: string): void {
    ConcourseDb.stopInstance(this.getFilePagesDbName(projectId, fileId));
  }

  static async getFilesSimple(projectId: string): Promise<FileDoc[]> {
    const db = FileService.getFilesDb(projectId);
    return navigator.onLine ? await db.getDocsAwaitSync() : await db.getDocs();
  }

  static async getFile(projectId: string, fileId: string): Promise<FileDoc> {
    const db = FileService.getFilesDb(projectId);
    return await db.getDocById(fileId);
  }

  static async getFilePageList(projectId: string, fileId: string, onComplete: (docs: FilePageDoc[]) => any, onChange: (e: ChangeEvent) => any): Promise<FilePageDoc[]> {
    const db = FileService.getFilePagesDb(projectId, fileId);

    db.on('complete', docs => onComplete(docs));
    db.on('complete', event => onChange(event));

    return await db.getDocs();
  }

  static async savePage(projectId: string, page: FilePageDoc): Promise<FilePageDoc> {
    const db = FileService.getFilePagesDb(projectId, page.file_id);
    return await db.saveDoc(page);
  }

  static async savePages(projectId: string, fileId: string, pages: FilePageDoc[]): Promise<FilePageDoc> {
    const db = FileService.getFilePagesDb(projectId, fileId);
    return await db.bulkDocs(pages);
  }

  static newFile(userId: string, parentId: string, type: FileType, name: string): FileDoc {
    return {
      _id: ConcourseDb.makeShortDocId(),
      type,
      parent_id: parentId,
      name: name,
      created_by: userId,
      created_date: new Date().toISOString(),
      updated_date: new Date().toISOString(),
    };
  }

  static async createFile(projectId: string, userId: string, parentId: string, type: FileType, name: string): Promise<FileDoc> {
    const db = FileService.getFilesDb(projectId);
    const file = FileService.newFile(userId, parentId, type, name);
    return await db.insert(file);
  }

  static async saveFile(projectId: string, file: FileDoc): Promise<FileDoc> {
    console.log(`FileService.saveFile() id:${file._id} _rev:${file._rev} name:${file.name}`);
    const db = FileService.getFilesDb(projectId);
    file.updated_date = new Date().toISOString();
    return await db.update(file);
  }

  static async savePhoto(projectId: string, photo: PhotoDoc): Promise<PhotoDoc> {
    const db = FileService.getFilesDb(projectId);
    photo.updated_date = new Date().toISOString();
    return await db.update(photo);
  }

  static async saveFiles(projectId: string, files: FileDoc[]): Promise<FileDoc[]> {
    const db = FileService.getFilesDb(projectId);
    const resp: PutResponse[] = await db.bulkDocs(files.map(f => ({ ...f, updated_date: new Date().toISOString() })));
    if (resp[0]['error']) {
      throw new Error(`Error saving files ${resp[0]['error']}`);
    }
    return files.map(f => ({ ...f, _rev: resp.find(r => r.id == f._id).rev }));
  }

  static async generateFilePages(projectId: string, file: FileDoc, roles: string[]): Promise<FileDoc> {
    const response = await authFetch(`${globalThis.ccLambdaBaseUrl}/fn/files/generate-file-pages`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getAccessToken()}`,
      },
      body: JSON.stringify({ projectId, file, roles }),
    });

    const data = await response.json();
    if (!response.ok || data.error) {
      throw new Error(data.error);
    }
    if (!data.file) {
      throw new Error("Couldn't generate file pages");
    }

    if (data.file) {
      return data.file as FileDoc;
    }
  }

  static async analyseFilePages(projectId: string, fileId: string, pageIds: string[], exampleNumber: string): Promise<PageAnalysis[]> {
    const response = await authFetch(`${globalThis.ccLambdaBaseUrl}/fn/files/analyseFilePages`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getAccessToken()}`,
      },
      body: JSON.stringify({ projectId, fileId, pageIds, exampleNumber }),
    });

    const data = await response.json();
    if (!response.ok || data.error) {
      throw new Error(data.error);
    } else {
      return data as PageAnalysis[];
    }
  }

  static async analyseSpecSection(projectId: string, fileId: string, start: number, count: number): Promise<SubmittalBasic[]> {
    const response = await authFetch(`${globalThis.ccLambdaBaseUrl}/fn/files/analyse-spec-section`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getAccessToken()}`,
      },
      body: JSON.stringify({ projectId, fileId, start, count }),
    });

    const data = await response.json();
    if (!response.ok || data.error) {
      throw new Error(data.error);
    } else {
      return data as SubmittalBasic[];
    }
  }

  static VERSION_THUMB: string = 'thumb';
  static VERSION_HIGHRES: string = 'highres';
  static VERSION_SVG: string = 'svg';

  /** Dispatched on the `<image>` when JPEG natural width/height are known (so the viewer can upgrade to SVG). */
  static readonly DRAWING_JPEG_NATURAL_SIZE_EVENT = 'cc-drawing-jpg-dimensions';

  static filesCache: LocalDb = new LocalDb('files-cache-4');
  static async getCacheValue(key: string): Promise<string> {
    return this.filesCache.getStringValue(key);
  }

  static async setCacheValue(key: string, value: string): Promise<void> {
    await this.filesCache.setStringValue(key, value);
  }

  static getMostRecentPageIndex(allFiles: FileDoc[], pages: FilePageDoc[]): number {
    const fileIds: string[] = pages.map(p => p.file_id);
    const files: FileDoc[] = allFiles.filter(f => fileIds.includes(f._id));
    const createdTimes: number[] = files.map(f => Date.parse(f.created_date));
    return createdTimes.indexOf(Math.max(...createdTimes));
  }

  static loadThumbImage(page: FilePageDoc, image: SVGImageElement) {
    console.log('loadThumbImage');
    image.setAttribute('href', `data:image/jpeg;base64,${page.thumb}`);
    image.setAttribute('data-version', FileService.VERSION_THUMB);
  }

  /** When S3 high-res/SVG is missing or denied (403), restore embedded thumb so the drawing stays usable. */
  private static revertDrawingPageImageToThumb(page: FilePageDoc, image: SVGImageElement, modal?: HTMLIonToastElement) {
    image.removeAttribute('data-naturalWidth');
    image.removeAttribute('data-naturalHeight');
    if (page.thumb) {
      FileService.loadThumbImage(page, image);
    } else {
      image.removeAttribute('data-version');
    }
    void modal?.dismiss();
  }

  static async getSignedUrl(s3Key: string): Promise<string> {
    const encodedKey = encodeURIComponent(s3Key);
    const res = await authInterceptor.GET(`${globalThis.ccLambdaBaseUrl}/fn/files/getSignedUrl?s3Key=${encodedKey}`);
    const json = await res.json();
    const url = json.url ?? json.signedUrl;
    if (!res.ok || !url) {
      const msg = json.error || json.message || `getSignedUrl failed: ${res.status}`;
      throw new Error(typeof msg === 'string' ? msg : JSON.stringify(json));
    }
    return url;
  }

  static getSignedRedirectUrl(s3Key: string): string {
    return `${globalThis.ccLambdaBaseUrl}/fn/files/getSignedRedirectUrl?s3Key=${encodeURIComponent(s3Key)}&token=${globalThis.couchdbToken}`;
  }

  static getSVGKey(projectId: string, fileId: string, pageId: string): string {
    return `files/${projectId}/${fileId}/${pageId}.svg`;
  }

  static async getSVGBase64(projectId: string, page: FilePageDoc): Promise<string> {
    return this.loadS3File(this.getSVGKey(projectId, page.file_id, page._id));
  }

  /**
   * Downloads multiple files as a zip archive
   * @param projectId The project ID
   * @param fileIds Array of file IDs to download
   * @returns Blob containing the zip file, or null if the download failed
   */
  static async downloadFilesAsZip(projectId: string, files: FileDoc[]): Promise<void> {
    if (!files || files.length === 0) {
      throw new Error('No files provided for download');
    }
    const totalBytes = files.reduce((accumulator, currentValue) => {
      return accumulator + currentValue.num_bytes;
    }, 0); // The '0' is the initial value for the accumulator
    if (totalBytes > 10000000) {
      throw new Error('Total file size exceeds 10MB limit');
    }

    const fileIds = files.map(file => file._id).join(',');
    window.open(`${globalThis.ccLambdaBaseUrl}/fn/files/download-files?projectId=${projectId}&fileIds=${fileIds}`, '_blank');
  }

  static async getHighResJpgBase64(projectId: string, page: FilePageDoc): Promise<string> {
    return this.loadS3File(`files/${projectId}/${page.file_id}/${page._id}.jpg`);
  }

  static async loadS3File(s3Key: string): Promise<string> {
    const cached = await FileService.getCacheValue(s3Key);
    if (cached) {
      return cached;
    }
    const imgUrl = await this.getSignedUrl(s3Key);
    const base64 = await this.loadFileToBase64(imgUrl);
    FileService.setCacheValue(s3Key, base64);
    return base64;
  }

  static async loadFileToBase64(url: string): Promise<string> {
    return new Promise((resolve, reject) => {
      var xhr = new XMLHttpRequest();
      xhr.open('GET', url, true);
      xhr.responseType = 'blob';
      xhr.onload = () => {
        if (xhr.status === 200) {
          const blob: Blob = xhr.response as Blob;
          blobToBase64(blob).then(base64 => {
            resolve(base64);
          });
        } else {
          reject(`Image loading failed: ${xhr.status}`);
        }
      };
      xhr.onloadstart = function () {
        console.log('starting load');
      };
      xhr.send();
    });
  }

  static async loadHighResImage(projectId: string, page: FilePageDoc, image: SVGImageElement) {
    const isLoading = await toastController.getTop();
    if (image.getAttribute('data-version') == FileService.VERSION_HIGHRES || isLoading) {
      console.log(`loadHighResImage ${page._id} skipped (already highres or loading)`);
      return;
    }

    if (!page.file_id) {
      FileService.loadThumbImage(page, image);
      return;
    }

    // Set the attribute to high res right away to stop loading again
    image.setAttribute('data-version', FileService.VERSION_HIGHRES);

    const s3Key = `files/${projectId}/${page.file_id}/${page._id}.jpg`;
    const cached = await FileService.getCacheValue(s3Key);
    if (cached) {
      console.log(`loadHighResImage ${page._id} using cache`);
      FileService.setHighResImage(image, cached);
      return;
    }

    console.log(`loadHighResImage ${page._id} starting`);
    let modal: HTMLIonToastElement | undefined;

    const finishSuccess = (blob: Blob) => {
      blobToBase64(blob).then(base64 => {
        FileService.setCacheValue(s3Key, base64);
        FileService.setHighResImage(image, base64);
      });
      void modal?.dismiss();
    };

    const failToThumb = () => {
      console.warn(`loadHighResImage ${page._id} failed for key ${s3Key}, using thumbnail`);
      FileService.revertDrawingPageImageToThumb(page, image, modal);
    };

    const fetchWithRetries = async (attempt: number) => {
      let imageUrl: string | undefined;
      for (let signAttempt = 0; signAttempt < 3; signAttempt++) {
        try {
          imageUrl = await this.getSignedUrl(s3Key);
          break;
        } catch (e) {
          if (signAttempt < 2) {
            console.warn('loadHighResImage getSignedUrl failed, will retry…', s3Key, e);
            await new Promise(r => setTimeout(r, 4000 * (signAttempt + 1)));
            continue;
          }
          console.warn('loadHighResImage getSignedUrl failed after retries', s3Key, e);
          failToThumb();
          return;
        }
      }
      if (!imageUrl) {
        failToThumb();
        return;
      }

      if (!modal) {
        modal = await toastController.create({ message: 'Loading high res image...' });
      }

      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('GET', imageUrl, true);
        xhr.responseType = 'blob';
        xhr.onload = () => {
          if (xhr.status === 200) {
            finishSuccess(xhr.response as Blob);
            resolve();
            return;
          }
          if ((xhr.status === 403 || xhr.status === 404) && attempt < 2) {
            console.warn(
              `loadHighResImage ${page._id} HTTP ${xhr.status} for ${s3Key} — often means the high-res file is not on S3 yet (CloudConvert). Retrying (${attempt + 1}/2)…`,
            );
            window.setTimeout(() => {
              void fetchWithRetries(attempt + 1).then(resolve).catch(reject);
            }, 4000 * (attempt + 1));
            return;
          }
          console.warn(`loadHighResImage ${page._id} HTTP ${xhr.status} for ${s3Key} (no more retries)`);
          failToThumb();
          resolve();
        };
        xhr.onerror = () => {
          if (attempt < 2) {
            window.setTimeout(() => {
              void fetchWithRetries(attempt + 1).then(resolve).catch(reject);
            }, 4000 * (attempt + 1));
            return;
          }
          console.warn(`loadHighResImage ${page._id} network error for ${s3Key}`);
          failToThumb();
          resolve();
        };
        xhr.onprogress = function (e) {
          if (modal && e.lengthComputable) {
            modal.message = `Loading high-res version: ${Math.round((e.loaded / e.total) * 100)}%`;
          }
        };
        xhr.onloadstart = function () {
          console.log('starting load');
          void modal?.present();
        };
        xhr.send();
      });
    };

    try {
      await fetchWithRetries(0);
    } catch (e) {
      console.warn('loadHighResImage unexpected', e);
      failToThumb();
    }
  }

  static setHighResImage(image: SVGImageElement, base64: string) {
    // load the image to get the width and height and set it so that we know when to load SVG
    var img = new Image();
    img.onload = () => {
      image.setAttribute('data-naturalWidth', img.naturalWidth.toString());
      image.setAttribute('data-naturalHeight', img.naturalHeight.toString());
      image.dispatchEvent(new CustomEvent(FileService.DRAWING_JPEG_NATURAL_SIZE_EVENT, { bubbles: true }));
    };
    img.src = `data:image/jpeg;base64,${base64}`;
    image.setAttribute('href', `data:image/jpeg;base64,${base64}`);
  }

  static async loadSvgImage(projectId: string, page: FilePageDoc, image: SVGImageElement) {
    console.log('loadSvgImage');
    //const image = this.getPageElement(page);
    //const isLoading = await toastController.getTop();
    if (image.getAttribute('data-version') == FileService.VERSION_SVG) {
      console.log('Already loaded/ing SVG');
      return;
    }

    if (!page.file_id) {
      FileService.loadThumbImage(page, image);
      return;
    }
    // Set the attribute to high res right away to stop loading again
    image.setAttribute('data-version', FileService.VERSION_SVG);

    const s3Key = `files/${projectId}/${page.file_id}/${page._id}.svg`;
    const cached = await FileService.getCacheValue(s3Key);
    if (cached) {
      console.log('SVG was cached', page._id);
      image.setAttribute('href', cached);
      return;
    }

    console.log('loadSvgImage:', page._id);
    let modal: HTMLIonToastElement | undefined;
    try {
      const svgUrl = await this.getSignedUrl(s3Key);
      modal = await toastController.create({ message: 'Loading SVG...' });
      const xhr = new XMLHttpRequest();
      xhr.open('GET', svgUrl, true);
      xhr.responseType = 'text';
      xhr.onload = () => {
        if (xhr.status === 200) {
          const utf8 = unescape(encodeURIComponent(xhr.responseText));
          const b64 = btoa(utf8);
          const dataUrl = `data:image/svg+xml;base64,${b64}`;

          // cache the response for the future
          FileService.setCacheValue(s3Key, dataUrl);
          image.setAttribute('href', dataUrl);
          void modal.dismiss();
        } else {
          console.warn(`loadSvgImage ${page._id} HTTP ${xhr.status}, using thumbnail`);
          FileService.revertDrawingPageImageToThumb(page, image, modal);
        }
      };
      xhr.onerror = () => {
        console.warn(`loadSvgImage ${page._id} network error, using thumbnail`);
        FileService.revertDrawingPageImageToThumb(page, image, modal);
      };
      xhr.onprogress = function (e) {
        if (modal && e.lengthComputable) {
          modal.message = `Loading SVG version: ${Math.round((e.loaded / e.total) * 100)}%`;
        }
      };
      xhr.onloadstart = function () {
        console.log('starting load');
        void modal.present();
      };
      xhr.send();
    } catch (e) {
      console.warn('loadSvgImage getSignedUrl failed', e);
      FileService.revertDrawingPageImageToThumb(page, image, modal);
    }
  }

  static async loadSvgToString(projectId: string, page: FilePageDoc): Promise<string> {
    console.log('loadSvgToString');
    const base64 = await this.getSVGBase64(projectId, page);
    return atob(base64);
  }

  static filterFiles(files: FileDoc[], searchTerm: string): FileDoc[] {
    if (!searchTerm || searchTerm.length < 0) return files; //.sort(this.sortByDate);
    const lowerSearchTerm = searchTerm.toLowerCase();
    return files.filter(file => this.filterFile(file, lowerSearchTerm)); //.sort(this.sortByDate);
  }

  static filterFile(file: FileDoc, searchTermLower: string): boolean {
    if (searchTermLower.startsWith('#')) return file.tags?.some(tag => tag.name.toLowerCase().includes(searchTermLower.substring(1)));
    return file.name?.toLowerCase().includes(searchTermLower);
  }

  static sortByCreatedDateAsc = (a: FileDoc, b: FileDoc) => (a.created_date < b.created_date ? -1 : 1);
  static sortByCreatedDateDes = (a: FileDoc, b: FileDoc) => (a.created_date > b.created_date ? -1 : 1);
  static sortByUpdatedDateAsc = (a: FileDoc, b: FileDoc) => (a.updated_date < b.updated_date ? -1 : 1);
  static sortById = (a: FilePageDoc, b: FilePageDoc) => (parseInt(a._id) < parseInt(b._id) ? -1 : 1);
  static sortByName = (a: FileDoc, b: FileDoc) => (a.name < b.name ? -1 : 1);

  static getAncestors(files: FileDoc[], file: FileDoc): FileDoc[] {
    if (!file) return [];
    const ancestors: FileDoc[] = [];
    let parent = files.find(f => f._id == file.parent_id);
    let attempts = 0;
    while (parent && attempts < 10) {
      ancestors.push(parent);
      parent = files.find(f => f._id == parent.parent_id);
      attempts++; // avoid infinite loop
    }
    return ancestors;
  }

  static getDescendents(files: FileDoc[], folderId: string): FileDoc[] {
    //if (!folderId) return [];
    const descendents: FileDoc[] = [];
    files.forEach(file => {
      if (file.parent_id == folderId) descendents.push(file);
      if (file.parent_id == folderId && (file.type == FileType.FOLDER || file.type == FileType.SET)) {
        const childDescendents = FileService.getDescendents(files, file._id);
        descendents.push(...childDescendents);
      }
    });
    return descendents;
  }

  static searchPages(pages: FilePageDoc[], searchTerm: string): SearchResult[] {
    return pages
      .map(page => {
        return {
          snippets: this.getSnippets(page, searchTerm),
          page: page,
        };
      })
      .filter(result => result.snippets.length > 0);
  }

  static getSnippets(page: FilePageDoc, substring: string, snippetLength: number = 100): string[] {
    const regex = new RegExp(substring, 'gi'); // Case-insensitive global search
    const results: string[] = [];

    let text = page.body || '';
    let breakChars = ['\n', '.', '"', '´', '“', '”', '-', ':', '_', '(', ')']; //,
    let match;
    while ((match = regex.exec(text)) !== null) {
      const matchIndex = match.index;
      const matchText = match[0];

      // Find the nearest space before snippetLength
      let start = matchIndex;
      while (start > 0 && !breakChars.includes(text[start - 1])) start--;

      let end = matchIndex + matchText.length;
      while (end < text.length && !breakChars.includes(text[end])) end++;

      const afterSnip = text.substring(matchIndex, snippetLength);
      const parts = text.split('\n');

      // Extract before, match, and after
      const before = text.substring(start, matchIndex);
      const after = text.substring(matchIndex + matchText.length, end);
      results.push(`${before}<strong>${matchText}</strong>${after}`.trim());
    }

    return results;
  }

  static async getPositionRects(projectId: string, document: Document, terms: string[], page: FilePageDoc): Promise<PctRect[]> {
    console.log('getPositionRects', terms, page._id);
    const svgString = await FileService.loadSvgToString(projectId, page);
    const svgDoc = new DOMParser().parseFromString(svgString, 'image/svg+xml');

    const textElements = svgDoc.querySelectorAll('text');
    if (!textElements || textElements.length == 0) return [];
    terms.forEach(searchTerm => {
      const regex = new RegExp(searchTerm, 'gi');
      textElements.forEach((textElement: SVGTextElement) => {
        const wrap = (match: string) => `<tspan class="hilight-text" fill="red">${match}</tspan>`;
        let replaced = FileService.replaceOutsideTags(textElement.innerHTML, regex, wrap);
        if (replaced == textElement.innerHTML) return; // no change
        console.log('replaced', replaced);
        textElement.innerHTML = replaced;
      });
    });

    const temp = document.createElement('div');
    temp.style.visibility = 'hidden';
    document.body.appendChild(temp);

    const documentElement = svgDoc.documentElement as unknown as SVGSVGElement;
    temp.appendChild(documentElement);

    const hilightedTexts = documentElement.getElementsByClassName('hilight-text');
    const docBounds = documentElement.getBoundingClientRect();
    const posRects = Array.from(hilightedTexts).map(element => {
      const bounds = element.getBoundingClientRect();
      return {
        text: element.textContent,
        t: bounds.top / docBounds.height,
        r: bounds.right / docBounds.width,
        b: bounds.bottom / docBounds.height,
        l: bounds.left / docBounds.width,
      };
    });

    document.body.removeChild(temp);
    return posRects;
  }

  static boundsToSvgRect(document: Document, width: number, height: number, bounds: PctRect): SVGRectElement {
    const top = height * bounds.t;
    const right = width * bounds.r;
    const bottom = height * bounds.b;
    const left = width * bounds.l;
    const rect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
    rect.setAttribute('class', `hilight-box`);
    rect.setAttribute('x', `${left}`);
    rect.setAttribute('y', `${top}`);
    rect.setAttribute('width', `${right - left}`);
    rect.setAttribute('height', `${bottom - top}`);
    rect.setAttribute('fill', 'red'); // No fill for the rectangle
    rect.setAttribute('fill-opacity', '0.4'); // Red border around the matched text
    return rect;
  }

  static getScrapbookThumbnailUrl(file: FileDoc) {
    const attachments = file['_attachments'];
    if (attachments && attachments['thumbnail']) {
      return `data:${attachments['thumbnail'].content_type};base64,${attachments['thumbnail'].data}`;
    }
    return null;
  }

  static getFileSize(bytes: number): string {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  static async generateFileMarkupsPdf(projectId: string, fileId: string, s3FileKey?: string): Promise<{ fileS3Key: string; signedUrl: string }> {
    const s3Key = s3FileKey ? `?s3Key=${s3FileKey}` : '';
    const response = await authFetch(`${globalThis.ccLambdaBaseUrl}/fn/files/${fileId}/project/${projectId}/download-pdf${s3Key}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getAccessToken()}`,
      },
    });

    const data = await response.json();
    if (!response.ok || data.error) {
      throw new Error(data.error || data.message);
    }

    const fileS3Key = data?.fileS3Key;
    const signedUrl = data?.signedUrl;
    if (!fileS3Key) {
      throw new Error('Error generating scrapbook pdf');
    }
    return { fileS3Key, signedUrl };
  }

  static async searchWithAI(projectId: string, fileId: string, query: string): Promise<AISearchResult[]> {
    const response = await authFetch(`${globalThis.ccLambdaBaseUrl}/fn/files/rag-store/${projectId}/${fileId}/${query}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getAccessToken()}`,
      },
    });
    const json = await response.json();
    if (!response.ok || json.error) {
      throw new Error(json.error);
    } else {
      return json.results as AISearchResult[];
    }
  }
}
